import { itemSizeBytes, technicalMetadataOf } from '../api/helpers';
import type { LibraryItem } from '../api/types';
import type { DownloadRecord } from './manager';

// Row metadata kept on download records (download rows and offline book
// cards), and the trimmed item JSON stored for offline book pages.

function yearOf(value: string | null | undefined): number | null {
  const n = Number(value);
  return value != null && value !== '' && Number.isFinite(n) ? Math.trunc(n) : null;
}

function firstSeriesName(item: LibraryItem): string | null {
  const series = item.media.metadata.series;
  const first = Array.isArray(series) ? series[0] : series;
  if (first?.name) return first.name;
  const minified = item.media.metadata.seriesName?.split(', ')[0]?.replace(/\s+#\S+$/, '');
  return minified || null;
}

/** Row metadata from the (full) item: size, codec, container, bitrate, series… */
export function rowMetadata(item: LibraryItem): Partial<DownloadRecord> {
  const md = item.media.metadata;
  const tech = technicalMetadataOf(item);
  return {
    libraryId: item.libraryId,
    subtitle: md.subtitle ?? null,
    seriesName: firstSeriesName(item),
    genres: md.genres ?? [],
    publishedYear: yearOf(md.publishedYear),
    addedAt: item.addedAt,
    updatedAt: item.updatedAt,
    sizeBytes: itemSizeBytes(item),
    codec: tech.codec,
    container: tech.container,
    bitrate: tech.bitrate,
  };
}

/** Backfills row metadata on records saved before these fields existed. */
export function withRowMetadata(r: DownloadRecord): DownloadRecord {
  if (r.libraryId !== undefined) return r;
  const meta = rowMetadata(r.item);
  const ext = r.tracks[0]?.fileName.split('.').pop()?.toLowerCase();
  return {
    ...r,
    ...meta,
    sizeBytes: meta.sizeBytes ?? (r.totalBytes || null),
    container: meta.container ?? (ext && ext !== r.tracks[0]?.fileName.toLowerCase() ? ext : null),
    coverBytes: r.coverBytes ?? r.coverBlob?.size,
  };
}

/** The item without audio file details (offline book pages need metadata and chapters only). */
export function stripItem(item: LibraryItem): LibraryItem {
  return {
    id: item.id,
    libraryId: item.libraryId,
    mediaType: item.mediaType,
    addedAt: item.addedAt,
    updatedAt: item.updatedAt,
    media: {
      // The book (media) id, which offline listening sessions report to the server.
      ...(item.media.id ? { id: item.media.id } : {}),
      metadata: item.media.metadata,
      duration: item.media.duration,
      chapters: item.media.chapters,
      tags: item.media.tags,
      numChapters: item.media.chapters?.length,
      size: item.media.size,
      // Kept so an offline book page knows whether the server has a cover (null = none).
      ...(item.media.coverPath !== undefined ? { coverPath: item.media.coverPath } : {}),
    },
  };
}
